import { useEffect, useState, useCallback } from 'react';
import { useCooperativeId } from 'hooks';
import { Document, DocumentsRes } from 'models';
import services from 'services';
import { saveAs } from 'file-saver';
import type { Dictionary } from 'lodash';
import omit from 'lodash/omit';
import keyBy from 'lodash/keyBy';
import Stack, { StackProps } from '@mui/material/Stack';

import { ServerFileList, Centered, Loader } from 'components';
import Dropzone, { DropzoneClasses } from 'components/dropzone';

export type FileLoaderClasses = Partial<{
  dropzone: DropzoneClasses;
}>;

interface DocumentLoading {
  DeleteLoading: boolean;
  DownloadLoading: boolean;
}

interface State {
  files: Dictionary<Document & DocumentLoading>;
  loading: boolean;
  error: boolean;
}

type EndpointProps = {
  endpoint: string;
  entityId?: undefined;
  caseId?: undefined;
};

type IdsProps = {
  endpoint?: undefined;
  entityId: string;
  caseId: string | null;
};

type PartialProps = EndpointProps | IdsProps;

type Props = {
  classes?: FileLoaderClasses;
  documents?: Document[];
  isError?: boolean;
  isLoading?: boolean;
  editable?: boolean;
  disabled?: boolean;
  StackProps?: StackProps;
  cachedEndpoint?: string;
  touchDocuments?: () => void;
} & PartialProps;

function modifyDocuments(
  documents: Document[]
): Dictionary<Document & DocumentLoading> {
  return keyBy(
    documents.map((document) => ({
      ...document,
      DeleteLoading: false,
      DownloadLoading: false,
    })),
    'Id'
  );
}

const FileLoader = ({
  classes: propClasses,
  documents,
  entityId,
  caseId,
  endpoint,
  cachedEndpoint,
  editable = true,
  disabled = false,
  isError = false,
  isLoading = false,
  StackProps,
  touchDocuments,
}: Props) => {
  const coopId = useCooperativeId();
  const [state, setState] = useState<State>({
    files: {},
    loading: true,
    error: false,
  });

  const url =
    endpoint ||
    `/api/user/cooperatives/${coopId}/documents/${entityId}/${caseId}`;

  const fetchFiles = useCallback(async () => {
    setState((prevState) => ({ ...prevState, loading: true, error: false }));

    const getURL = cachedEndpoint ?? url;

    try {
      const res: DocumentsRes[] = (await services.get(getURL)).data.Data;
      setState((prevState) => ({
        ...prevState,
        files: res && res.length ? modifyDocuments(res[0].Documents) : {},
        loading: false,
        upload: false,
      }));
    } catch (err) {
      console.error(err);
      setState((prevState) => ({ ...prevState, loading: false, error: true }));
    }
  }, [url]);

  const setFileDeleting = (
    id: string,
    flag: boolean,
    field: 'DeleteLoading' | 'DownloadLoading'
  ) => {
    setState((prevState) => ({
      ...prevState,
      files: {
        ...prevState.files,
        [id]: {
          ...prevState.files[id],
          [field]: flag,
        },
      },
    }));
  };

  const removeFileFromList = (id: string) => {
    setState((prevState) => ({
      ...prevState,
      files: omit(prevState.files, id),
    }));
  };

  const handleDelete = async (id: string) => {
    setFileDeleting(id, true, 'DeleteLoading');
    try {
      const res = await services.delete(`${url}?fileUrl=${id}`);
      if (res.status === 204) {
        if (typeof touchDocuments === 'function') {
          touchDocuments();
        }
        removeFileFromList(id);
      }
    } catch (err) {
      console.error(err);
      setFileDeleting(id, false, 'DeleteLoading');
    }
  };

  const handleUpload = async (files: File[]) => {
    const formData = new FormData();

    for (const file of files) {
      formData.append('file', file, file.name);
    }

    try {
      const res = await services.post(url, formData);

      if (res.status === 200 && typeof touchDocuments === 'function') {
        touchDocuments();
      }

      await fetchFiles();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDownloadFile = useCallback(
    async (id: string, name: string) => {
      try {
        setFileDeleting(id, true, 'DownloadLoading');

        const { data } = await services.get(
          `${url}/download?fileUrl=${encodeURIComponent(id)}`,
          {
            responseType: 'blob',
            headers: {
              Accept: '*/*',
            },
          }
        );

        saveAs(data, name);
        setFileDeleting(id, false, 'DownloadLoading');
      } catch (err) {
        console.error(err);
        setFileDeleting(id, false, 'DownloadLoading');
      }
    },
    [url]
  );

  useEffect(() => {
    if (!Array.isArray(documents)) {
      fetchFiles();
    } else {
      setState({
        error: isError,
        loading: isLoading,
        files: modifyDocuments(documents),
      });
    }
  }, [documents, fetchFiles, isError, isLoading]);

  if (state.loading) {
    return (
      <Centered>
        <Loader themeColor="darkBlue" />
      </Centered>
    );
  }

  return (
    <Stack spacing={2} {...StackProps}>
      {editable && (
        <Dropzone
          classes={propClasses?.dropzone}
          uploadFiles={handleUpload}
          disabled={disabled}
        />
      )}
      <ServerFileList
        downloadFile={handleDownloadFile}
        documents={Object.values(state.files)}
        handleDelete={handleDelete}
      />
    </Stack>
  );
};

export default FileLoader;
